import { supabase } from './supabase';

// Generate a cryptographically secure code verifier
const generateCodeVerifier = (length = 128) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => 
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'[byte % 66]
  ).join('');
};

// Base64-URL encode an ArrayBuffer
const base64UrlEncode = (buffer: ArrayBuffer): string => {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// Generate PKCE code challenge from verifier
const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

// Generate a secure state parameter
const generateState = (): string => {
  const stateArray = new Uint8Array(32);
  crypto.getRandomValues(stateArray);
  return base64UrlEncode(stateArray.buffer);
};

export const startLinkedInOAuth = async () => {
  try {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      throw new Error('Not authenticated');
    }
    
    // Generate PKCE code verifier and challenge
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    
    // Generate state parameter
    const state = generateState();
    
    // Store values in cookies
    document.cookie = `user_id=${user.id}; path=/; secure; samesite=lax; max-age=3600`;
    document.cookie = `code_verifier=${codeVerifier}; path=/; secure; samesite=lax; max-age=3600`;
    document.cookie = `oauth_state=${state}; path=/; secure; samesite=lax; max-age=3600`;
    
    // Construct LinkedIn authorization URL
    const clientId = import.meta.env.VITE_LINKEDIN_CLIENT_ID;
    const redirectUri = 'https://serge.unleashsupport.com/.netlify/functions/linkedinCallback';
    const scope = 'openid profile email w_member_social';
    
    const authUrl = new URL('https://www.linkedin.com/oauth/v2/authorization');
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('client_id', clientId);
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('state', state);
    authUrl.searchParams.append('scope', scope);
    authUrl.searchParams.append('code_challenge', codeChallenge);
    authUrl.searchParams.append('code_challenge_method', 'S256');
    
    // Redirect to LinkedIn's authorization page
    window.location.href = authUrl.toString();
  } catch (err) {
    console.error('Error initiating LinkedIn OAuth flow:', err);
    throw err;
  }
};